import React, { useCallback, useMemo } from 'react';
import { useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import Button from '@xcritical/button';

import { crmGridReset, useGrid } from '@crm-framework/grid';
import { Spacing } from '@crm-framework/spacing';
import { Dropdown, IDropdownItem } from '@crm-framework/dropdown';
import { SelectFormField, ISelectOption } from '@crm-framework/form-fields';

import { FilterButtonWrapper, NotificationsLayout } from './styles';
import {
  getBUSelectOptions,
  getIsBUFilterDisabled,
  getNotificationData,
} from './selectors';
import { notificationsActions } from './NotificationsStore';
import {
  NOTIFICATIONS_GRID_NAME,
  NOTIFICATION_TYPES,
  NOTIFICATION_TYPE_ALL,
} from './consts';
import { NotificationSkeleton } from './components';
import { NotificationGridComponent } from './NotificationGridComponent';
import { INotificationTypeFromServer } from './types';

const setIsBUFilterDisabled = (params: {
  eventType: NOTIFICATION_TYPES | 'All';
  notificationTypes: INotificationTypeFromServer[];
  dispatch: Dispatch<any>;
}) => {
  const isBUFilterNotDisabled =
    params.eventType === 'All' ||
    params.notificationTypes.find(({ alias }) => alias === params.eventType)
      ?.singleClient;

  params.dispatch(
    notificationsActions.setIsBuFilterDisabled(!isBUFilterNotDisabled)
  );
};

export const Notifications: React.FC = () => {
  const dispatch = useDispatch();

  const { page } = useGrid(NOTIFICATIONS_GRID_NAME);

  const { isReady, isLoading, eventType, notificationTypes } =
    useSelector(getNotificationData);

  const businessUnitOptions = useSelector(getBUSelectOptions);

  const isBUFilterDisabled = useSelector(getIsBUFilterDisabled);

  const eventTypeItems: IDropdownItem[] = useMemo(() => {
    const defaultItems = [{ value: NOTIFICATION_TYPE_ALL, label: 'All' }];

    notificationTypes.forEach((notificationType) => {
      defaultItems.push({
        value: notificationType.alias,
        label: notificationType.name,
      });
    });

    return defaultItems;
  }, [notificationTypes]);

  useUnmount(() => {
    if (eventType === NOTIFICATION_TYPE_ALL && page === 1) {
      return;
    }

    dispatch(notificationsActions.reset());
    dispatch(crmGridReset(NOTIFICATIONS_GRID_NAME));
    dispatch(notificationsActions.startInit());
  });

  const onChangeEventType = useCallback(
    (value: NOTIFICATION_TYPES) => {
      setIsBUFilterDisabled({
        eventType: value,
        notificationTypes,
        dispatch,
      });

      dispatch(notificationsActions.setEventType({ eventType: value }));
    },
    [dispatch, notificationTypes]
  );

  const onBUChange = useCallback((value: ISelectOption[]) => {
    dispatch(notificationsActions.setBUFilterValue(value));
  }, []);

  const filter = useCallback(
    () => dispatch(notificationsActions.refresh()),
    []
  );

  if (!isReady) {
    return <NotificationSkeleton />;
  }

  return (
    <NotificationsLayout>
      <Spacing mb={10}>
        <Dropdown
          appearance="itemList"
          label="Event Type:"
          disabled={isLoading}
          value={eventType}
          items={eventTypeItems}
          onChange={onChangeEventType}
        />
      </Spacing>
      <Spacing mb={10}>
        <SelectFormField
          placeholder="Business Unit"
          options={businessUnitOptions}
          isMulti
          onChange={onBUChange}
          disabled={isBUFilterDisabled}
        />
      </Spacing>
      <Spacing mb={10}>
        <FilterButtonWrapper>
          <Button appearance="primary" onClick={filter}>
            Filter
          </Button>
        </FilterButtonWrapper>
      </Spacing>
      <NotificationGridComponent />
    </NotificationsLayout>
  );
};
